import React, { useEffect, useState } from 'react'
import { Autocomplete, TextField, CircularProgress } from '@mui/material'
import { Role } from '../../models/User'
import { v4 as uuidv4 } from 'uuid'; 

export interface AutocompleteAsyncTextFieldProps<Entity> {
  label: string
  role?: Role
  roleId?: Number
  search: (input: string, role?: Role, roleId?: Number) => Promise<Entity[]>
  selectLabel: (option: Entity) => string
  onSelect: (option: Entity | null) => void
  newEntity?: (input: string) => Entity
  inputValue?: string
  style?: any
  disableClearable?: boolean
  required?: boolean
  controlledLength?: number
}


const AutocompleteAsyncTextField = <T extends {}>({ label, search, role, roleId, selectLabel, onSelect, newEntity, inputValue, disableClearable, style, controlledLength, ...props }: AutocompleteAsyncTextFieldProps<T>) => {
  const [open, setOpen] = useState(false)
  const [options, setOptions] = useState<T[]>([])
  const [loading, setLoading] = useState(false)
  const [resetKey, setResetKey] = useState(0)

  const fetchOptions = async (input: string) => {
    if (open) {
      setLoading(true)
      const searchedOptions = await search(input, role, roleId)

      // Agrega un ID único a cada opción si no tiene uno
      const optionsWithUniqueId = searchedOptions
        ? searchedOptions.map((option) => ({
          ...option,
          id: (option as any).id || uuidv4(), // Usa el ID existente o genera uno nuevo
        }))
        : [];

      setOptions(optionsWithUniqueId);

      //setOptions(searchedOptions ? searchedOptions : [])
      setLoading(false)
    }
  }

  //This is needed to reset the value selected to null when the filters are cleaned
  useEffect(() => {
    if (!inputValue) setResetKey(prev => prev + 1)
  }, [inputValue])

  return (
    <Autocomplete
      {...props}
      disableClearable={disableClearable}
      open={open}
      key={resetKey}
      onOpen={() => {
        setOpen(true)
      }}
      onClose={() => {
        setOpen(false)
      }}
      //value={value}
      getOptionLabel={(option) => selectLabel(option)}
      options={options}
      loading={loading}
      style={{ ...style, minWidth: '300px' }}
      onChange={(event, value) => { onSelect(value) }}
      onInputChange={(event, newInputValue) => {
        if (!controlledLength || (newInputValue && newInputValue.length >= controlledLength))
          fetchOptions(newInputValue)
      }}
      filterOptions={(options, params) => {
        // Suggest the creation of a new value
        if (newEntity && !options.length && params.inputValue) {
          options.push(newEntity(params.inputValue))
        }
        return options
      }}
      renderOption={(props, option) => {
        // Usa el ID único como key
        return (
          <li {...props} key={(option as any).id}>
            {selectLabel(option)}
          </li>
        );
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          name={label}
          //onChange={(event) => { fetchOptions(event.target.value) }}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
          required={props.required}
        />
      )}
    />
  )
}

export default AutocompleteAsyncTextField
